import { useCallback, useEffect, useRef, useState } from 'react';
import { articleAnchorId } from '../../lib/briefModel.js';
import { scrollToBriefTarget } from './briefScroll.js';

/**
 * Highlights the article in view in the sidebar; click navigates via hash scroll.
 */
export function useBriefScrollSpy(ready, articles = []) {
  const [activeArticleId, setActiveArticleId] = useState(null);
  const observerRef = useRef(null);

  const articleIds = articles.map((a) => a.articleId).filter(Boolean);

  const scrollToArticle = useCallback((articleId) => {
    if (!articleId) return;
    const hash = `#${articleAnchorId(articleId)}`;
    scrollToBriefTarget(hash);
    setActiveArticleId(articleId);
  }, []);

  useEffect(() => {
    if (!ready || articleIds.length === 0) {
      setActiveArticleId(null);
      return undefined;
    }

    const elements = articleIds
      .map((id) => document.getElementById(articleAnchorId(id)))
      .filter(Boolean);

    if (elements.length === 0) return undefined;

    const visible = new Map();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id.replace(/^article-/, '');
          if (entry.isIntersecting) {
            visible.set(id, entry.intersectionRatio);
          } else {
            visible.delete(id);
          }
        });

        if (visible.size === 0) return;

        let bestId = null;
        let bestRatio = -1;
        visible.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActiveArticleId(bestId);
      },
      {
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    elements.forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, [ready, articleIds.join(',')]);

  return { activeArticleId, scrollToArticle };
}
